import ConfirmDialog from '../components/ConfirmDialog';
import React, { useState, useEffect } from 'react';
import { toast } from '../components/Toast';
import Layout from '../components/Layout';

const API = 'https://api.safecorepest.com/api';

const LICENSE_TYPES = {
  NY: ['Commercial Technician', 'Commercial Applicator'],
  NJ: ['Pesticide Operator', 'Commercial Applicator']
};


// Helper: get expiry badge for a list of licenses
const getLicenseExpiryBadge = (licenses) => {
  if (!licenses || licenses.length === 0) return null;
  const today = new Date();
  let mostUrgent = null;
  for (const lic of licenses) {
    if (!lic.expiration_date) continue;
    const exp = new Date(lic.expiration_date + 'T00:00:00');
    const days = Math.floor((exp - today) / (1000 * 60 * 60 * 24));
    if (!mostUrgent || days < mostUrgent.days) {
      mostUrgent = { days, state: lic.state, license_number: lic.license_number };
    }
  }
  if (!mostUrgent) return null;
  const { days, state } = mostUrgent;
  if (days < 0) return { color: '#DC2626', bg: '#FEE2E2', text: `${state} EXPIRED`, icon: '🔴' };
  if (days <= 30) return { color: '#DC2626', bg: '#FEE2E2', text: `${state} exp in ${days}d`, icon: '🔴' };
  if (days <= 60) return { color: '#D97706', bg: '#FEF3C7', text: `${state} exp in ${days}d`, icon: '🟡' };
  if (days <= 90) return { color: '#2563EB', bg: '#DBEAFE', text: `${state} exp in ${days}d`, icon: '🔵' };
  return null;
};

const Technicians = () => {
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'orgchart'
  const [showModal, setShowModal] = useState(false);
  const [editingTech, setEditingTech] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    role: 'technician', is_technician: false, password: '', supervisor_id: ''
  });

  // License states
  const [hasLicense, setHasLicense] = useState(false);
  const [licenses, setLicenses] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [showLicenseForm, setShowLicenseForm] = useState(false);
  const [editingLicense, setEditingLicense] = useState(null);
  const [licenseForm, setLicenseForm] = useState({
    state: 'NY', license_number: '', license_type: 'Commercial Technician',
    issued_date: '', expiration_date: '', category_ids: []
  });

  // Supervisors = users who have licenses (fetched separately)
  const [supervisors, setSupervisors] = useState([]);

  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => { fetchTechnicians(); fetchCategories(); }, []);

  const fetchTechnicians = async () => {
    try {
      const res = await fetch(`${API}/users`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      setTechnicians((data.data || []).filter(u => !['super_admin'].includes(u.role)));
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API}/license-categories`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      setAllCategories(data.data || []);
    } catch (err) { console.error(err); }
  };

  const fetchLicenses = async (userId) => {
    try {
      const res = await fetch(`${API}/users/${userId}/licenses`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      const lics = data.data || [];
      setLicenses(lics);
      setHasLicense(lics.length > 0);
      return lics;
    } catch (err) { console.error(err); return []; }
  };

  const fetchSupervisors = async (excludeId) => {
    // Load all active users for org hierarchy (Reports To)
    try {
      const res = await fetch(`${API}/users`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      const allUsers = data.data || [];
      // All active users except current one can be a supervisor in the hierarchy
      const supervisorList = allUsers
        .filter(u => u.id !== excludeId && u.is_active)
        .map(u => ({ ...u, licenses: [] }));
      // Fetch licenses in background for the "no license" warning display
      for (const u of supervisorList) {
        try {
          const licRes = await fetch(`${API}/users/${u.id}/licenses`, { headers: { 'Authorization': `Bearer ${token}` } });
          const licData = await licRes.json();
          u.licenses = licData.data || [];
        } catch (e) { /* skip */ }
      }
      setSupervisors(supervisorList);
    } catch (err) { console.error(err); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingTech ? `${API}/users/${editingTech.id}` : `${API}/users`;
      const method = editingTech ? 'PUT' : 'POST';
      const payload = { ...formData, company_id: user.company_id };
      if (editingTech && !formData.password) delete payload.password;
      // supervisor_id = org hierarchy (Reports To) — siempre se envía independiente de licencia
      payload.supervisor_id = formData.supervisor_id || null;
      
      const res = await fetch(url, {
        method, headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      if (res.ok) { setShowModal(false); setEditingTech(null); resetForm(); fetchTechnicians(); }
      else { const err = await res.json(); toast.error(err.message || 'Error saving'); }
    } catch (err) { toast.error('Error: ' + err.message); }
  };

  const handleEdit = async (tech) => {
    setEditingTech(tech);
    setFormData({
      first_name: tech.first_name, last_name: tech.last_name,
      email: tech.email, phone: tech.phone || '', role: tech.role,
      job_title: tech.job_title || '',
      is_technician: tech.is_technician || false, password: '',
      supervisor_id: tech.supervisor_id || ''
    });
    setShowLicenseForm(false);
    setEditingLicense(null);
    const lics = await fetchLicenses(tech.id);
    if (tech.is_technician) {
      setHasLicense(lics.length > 0);
    }
    // Siempre cargar la lista de supervisores para el dropdown "Reports To"
    fetchSupervisors(tech.id);
    setShowModal(true);
  };

  const handleToggleActive = async (tech) => {
    setConfirmDialog({ message: `${tech.is_active ? 'Deactivate' : 'Activate'} ${tech.first_name} ${tech.last_name}?`, confirmText: tech.is_active ? 'Deactivate' : 'Activate', confirmColor: tech.is_active ? '#DC2626' : '#2D5C3F', icon: tech.is_active ? '🔴' : '🟢', onConfirm: async () => {
      setConfirmDialog(null);
      try {
        await fetch(`${API}/users/${tech.id}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ is_active: !tech.is_active })
        });
        fetchTechnicians();
      } catch (err) { toast.error('Error: ' + err.message); }
    }});
  };

  const resetForm = () => {
    setFormData({ first_name: '', last_name: '', email: '', phone: '', role: 'technician', job_title: '', is_technician: false, password: '', supervisor_id: '' });
    setLicenses([]); setShowLicenseForm(false); setEditingLicense(null); setHasLicense(false); setSupervisors([]);
  };

  const openNewModal = () => { setEditingTech(null); resetForm(); fetchSupervisors(null); setShowModal(true); };

  const handleHasLicenseChange = (checked) => {
    setHasLicense(checked);
    if (!checked && editingTech) {
      fetchSupervisors(editingTech.id);
      setFormData(prev => ({ ...prev, supervisor_id: prev.supervisor_id || '' }));
    }
    if (checked) {
      setFormData(prev => ({ ...prev, supervisor_id: '' }));
    }
  };

  // License handlers
  const openLicenseForm = (lic = null) => {
    if (lic) {
      setEditingLicense(lic);
      setLicenseForm({
        state: lic.state, license_number: lic.license_number,
        license_type: lic.license_type || 'Commercial Technician',
        issued_date: lic.issued_date ? lic.issued_date.substring(0, 10) : '',
        expiration_date: lic.expiration_date ? lic.expiration_date.substring(0, 10) : '',
        category_ids: lic.categories.map(c => c.id)
      });
    } else {
      setEditingLicense(null);
      setLicenseForm({ state: 'NY', license_number: '', license_type: 'Commercial Technician', issued_date: '', expiration_date: '', category_ids: [] });
    }
    setShowLicenseForm(true);
  };

  const handleLicenseSubmit = async () => {
    if (!licenseForm.license_number) { toast.error('License number is required'); return; }
    try {
      const url = editingLicense
        ? `${API}/licenses/${editingLicense.id}`
        : `${API}/users/${editingTech.id}/licenses`;
      const method = editingLicense ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method, headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(licenseForm)
      });
      if (res.ok) { setShowLicenseForm(false); setEditingLicense(null); fetchLicenses(editingTech.id); }
      else { const err = await res.json(); toast.error(err.message || 'Error saving license'); }
    } catch (err) { toast.error('Error: ' + err.message); }
  };

  const handleDeleteLicense = async (licId) => {
    setConfirmDialog({ message: 'Delete this license? This cannot be undone.', confirmText: 'Delete', icon: '🗑', onConfirm: async () => {
      setConfirmDialog(null);
    try {
      await fetch(`${API}/licenses/${licId}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
      fetchLicenses(editingTech.id);
    } catch (err) { toast.error('Error: ' + err.message); }
    }});
  };

  const toggleCategory = (catId) => {
    setLicenseForm(prev => ({
      ...prev,
      category_ids: prev.category_ids.includes(catId)
        ? prev.category_ids.filter(id => id !== catId)
        : [...prev.category_ids, catId]
    }));
  };

  const handleStateChange = (newState) => {
    const types = LICENSE_TYPES[newState] || ['Commercial Applicator'];
    setLicenseForm({ ...licenseForm, state: newState, license_type: types[0], category_ids: [] });
  };

  const filteredCategories = (() => {
    const byState = allCategories.filter(c => c.state === licenseForm.state);
    const type = licenseForm.license_type;
    const isTechnicianOnly = type === 'Commercial Technician' || type === 'Pesticide Operator';
    if (isTechnicianOnly) return byState.filter(c => c.code === 'CORE' || c.code === '7A');
    return byState;
  })();

  const roleColors = {
    technician: { bg: '#DBEAFE', color: '#1E40AF' }, manager: { bg: '#FEF3C7', color: '#92400E' },
    router: { bg: '#E0E7FF', color: '#3730A3' }, supervisor: { bg: '#D1FAE5', color: '#065F46' },
    admin: { bg: '#FEE2E2', color: '#991B1B' }, customer_service: { bg: '#F3E8FF', color: '#6B21A8' },
  };

  // Get supervisor display for table
  const getSupervisorName = (tech) => {
    if (tech.supervisor_first_name) return `${tech.supervisor_first_name} ${tech.supervisor_last_name}`;
    return null;
  };

  // ── Org Chart helpers ─────────────────────────────────────────────────
  const buildOrgTree = (users) => {
    const map = {};
    users.forEach(u => { map[u.id] = { ...u, children: [] }; });
    const roots = [];
    users.forEach(u => {
      if (u.supervisor_id && map[u.supervisor_id]) {
        map[u.supervisor_id].children.push(map[u.id]);
      } else {
        roots.push(map[u.id]);
      }
    });
    return roots;
  };

  const roleOrder = { super_admin: 0, admin: 1, manager: 2, supervisor: 3, router: 4, customer_service: 4, technician: 5 };
  const roleColors2 = {
    admin: { bg: '#1A3A2A', color: 'white' },
    super_admin: { bg: '#111827', color: 'white' },
    manager: { bg: '#D4AF37', color: '#1A1A1A' },
    supervisor: { bg: '#2D5C3F', color: 'white' },
    technician: { bg: '#DBEAFE', color: '#1D4ED8' },
    router: { bg: '#F3E8FF', color: '#7C3AED' },
    customer_service: { bg: '#FEF3C7', color: '#92400E' },
  };

  const OrgNode = ({ node, depth = 0 }) => {
    const rc = roleColors2[node.role] || { bg: '#F3F4F6', color: '#374151' };
    const licBadge = node.license_count > 0;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0' }}>
        {/* Connector line from parent */}
        {depth > 0 && <div style={{ width: '2px', height: '20px', background: '#D1D5DB' }} />}
        {/* Node card */}
        <div style={{ background: 'white', border: '2px solid ' + rc.bg, borderRadius: '10px',
          padding: '12px 16px', minWidth: '160px', maxWidth: '200px', textAlign: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)', position: 'relative' }}>
          {/* Avatar */}
          <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: rc.bg,
            color: rc.color, display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '16px', fontWeight: '700', margin: '0 auto 8px' }}>
            {node.first_name?.[0]}{node.last_name?.[0]}
          </div>
          <div style={{ fontSize: '13px', fontWeight: '700', color: '#111827', lineHeight: '1.3' }}>
            {node.first_name} {node.last_name}
          </div>
          <div style={{ marginTop: '6px' }}>
            <span style={{ background: rc.bg, color: rc.color, padding: '2px 8px',
              borderRadius: '10px', fontSize: '11px', fontWeight: '600' }}>
              {node.job_title || node.role.charAt(0).toUpperCase() + node.role.slice(1).replace('_', ' ')}
            </span>
          </div>
          {licBadge && (
            <div style={{ marginTop: '4px', fontSize: '11px', color: '#059669', fontWeight: '600' }}>
              ✓ Licensed
            </div>
          )}
          {!node.is_active && (
            <div style={{ position: 'absolute', top: '6px', right: '6px', width: '8px', height: '8px',
              borderRadius: '50%', background: '#DC2626' }} />
          )}
        </div>
        {/* Children */}
        {node.children && node.children.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ width: '2px', height: '20px', background: '#D1D5DB' }} />
            {/* Horizontal connector if multiple children */}
            {node.children.length > 1 && (
              <div style={{ position: 'relative', display: 'flex', alignItems: 'flex-start',
                gap: '16px', paddingTop: '0' }}>
                {/* Top horizontal line */}
                <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
                  width: `calc(100% - 80px)`, height: '2px', background: '#D1D5DB' }} />
                {node.children
                  .sort((a, b) => (roleOrder[a.role] || 5) - (roleOrder[b.role] || 5))
                  .map(child => (
                    <OrgNode key={child.id} node={child} depth={depth + 1} />
                  ))}
              </div>
            )}
            {node.children.length === 1 && (
              <OrgNode key={node.children[0].id} node={node.children[0]} depth={depth + 1} />
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <Layout>
      <div style={styles.page}>
        <div style={styles.pageHeader}>
          <div>
            <h2 style={styles.pageTitle}>Technicians & Staff</h2>
            <p style={styles.pageSubtitle}>{technicians.length} team members</p>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <div style={{ display: 'flex', border: '1px solid #E5E7EB', borderRadius: '8px', overflow: 'hidden' }}>
              <button onClick={() => setViewMode('list')}
                style={{ padding: '8px 16px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: '600',
                  background: viewMode === 'list' ? '#2D5C3F' : 'white',
                  color: viewMode === 'list' ? 'white' : '#6B7280' }}>
                ☰ List
              </button>
              <button onClick={() => setViewMode('orgchart')}
                style={{ padding: '8px 16px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: '600',
                  background: viewMode === 'orgchart' ? '#2D5C3F' : 'white',
                  color: viewMode === 'orgchart' ? 'white' : '#6B7280' }}>
                🌳 Org Chart
              </button>
            </div>
            <button onClick={openNewModal} style={styles.addBtn}>+ Add Team Member</button>
          </div>
        </div>

        {/* ORG CHART VIEW */}
        {viewMode === 'orgchart' && (
          <div style={{ background: 'white', borderRadius: '10px', border: '1px solid #E8E8E8',
            padding: '32px 24px', overflowX: 'auto' }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#9CA3AF' }}>Loading...</div>
            ) : technicians.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#9CA3AF' }}>No team members found</div>
            ) : (
              <div>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '32px', flexWrap: 'wrap', gap: '12px' }}>
                  {Object.entries(roleColors2).map(([role, rc]) => (
                    technicians.some(t => t.role === role) && (
                      <span key={role} style={{ background: rc.bg, color: rc.color, padding: '3px 10px',
                        borderRadius: '10px', fontSize: '12px', fontWeight: '600' }}>
                        {role.charAt(0).toUpperCase() + role.slice(1).replace('_', ' ')}
                      </span>
                    )
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '40px', justifyContent: 'center',
                  alignItems: 'flex-start', flexWrap: 'wrap', minWidth: 'max-content', margin: '0 auto' }}>
                  {buildOrgTree(technicians)
                    .sort((a, b) => (roleOrder[a.role] || 5) - (roleOrder[b.role] || 5))
                    .map(root => (
                      <OrgNode key={root.id} node={root} depth={0} />
                    ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* LIST VIEW */}
        {viewMode === 'list' && loading ? (
          <div style={styles.loading}>Loading...</div>
        ) : viewMode === 'list' && (
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>Phone</th>
                  <th style={styles.th}>Role</th>
                  <th style={styles.th}>Field Tech</th>
                  <th style={styles.th}>Licensed / Supervisor</th>
                  <th style={styles.th}>Status</th>
                  <th style={{...styles.th, textAlign: 'center'}}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {technicians.map((tech) => {
                  const rc = roleColors[tech.role] || { bg: '#F3F4F6', color: '#374151' };
                  const supName = getSupervisorName(tech);
                  return (
                    <tr key={tech.id} style={styles.tr}>
                      <td style={styles.td}>
                        <div style={styles.nameCell}>
                          <div style={styles.avatar}>{tech.first_name?.[0]}{tech.last_name?.[0]}</div>
                          <span style={styles.nameText}>{tech.first_name} {tech.last_name}</span>
                        </div>
                      </td>
                      <td style={styles.td}>{tech.email}</td>
                      <td style={styles.td}>{tech.phone || '\u2014'}</td>
                      <td style={styles.td}>
                        <span style={{...styles.badge, backgroundColor: rc.bg, color: rc.color}}>{tech.role}</span>
                      </td>
                      <td style={styles.td}>
                        <span style={{
                          padding: '2px 8px', borderRadius: '12px', fontSize: '12px',
                          backgroundColor: tech.is_technician ? '#D1FAE5' : '#FEE2E2',
                          color: tech.is_technician ? '#065F46' : '#991B1B'
                        }}>{tech.is_technician ? 'Yes' : 'No'}</span>
                      </td>
                      <td style={styles.td}>
                        {tech.is_technician ? (
                          supName ? (
                            <span style={{ fontSize: '12px', color: '#92400E', backgroundColor: '#FEF3C7', padding: '2px 8px', borderRadius: '12px' }}>
                              Under: {supName}
                            </span>
                          ) : tech.license_count > 0 ? (
                            <span style={{ fontSize: '12px', color: '#065F46', backgroundColor: '#D1FAE5', padding: '2px 8px', borderRadius: '12px' }}>
                              Licensed
                            </span>
                          ) : (
                            <span style={{ fontSize: '12px', color: '#DC2626', backgroundColor: '#FEE2E2', padding: '2px 8px', borderRadius: '12px' }}>
                              Not Licensed
                            </span>
                          )
                        ) : '\u2014'}
                      </td>
                      <td style={styles.td}>
                        <span style={{...styles.badge,
                          backgroundColor: tech.is_active ? '#D1FAE5' : '#FEE2E2',
                          color: tech.is_active ? '#065F46' : '#991B1B',
                        }}>{tech.is_active ? 'Active' : 'Inactive'}</span>
                      </td>
                      <td style={{...styles.td, textAlign: 'center'}}>
                        <button onClick={() => handleEdit(tech)} style={styles.actionBtn}>Edit</button>
                        <button onClick={() => handleToggleActive(tech)}
                          style={{...styles.actionBtn, color: tech.is_active ? '#DC2626' : '#059669'}}>
                          {tech.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div style={styles.overlay} onClick={() => setShowModal(false)}>
            <div style={{...styles.modal, width: editingTech ? '720px' : '560px'}} onClick={e => e.stopPropagation()}>
              <div style={styles.modalHeader}>
                <h3 style={styles.modalTitle}>{editingTech ? 'Edit Team Member' : 'Add New Team Member'}</h3>
                <button onClick={() => setShowModal(false)} style={styles.closeBtn}>{'\u2715'}</button>
              </div>
              <form onSubmit={handleSubmit} style={styles.form}>
                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>First Name *</label>
                    <input style={styles.input} required value={formData.first_name}
                      onChange={e => setFormData({...formData, first_name: e.target.value})} />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Last Name *</label>
                    <input style={styles.input} required value={formData.last_name}
                      onChange={e => setFormData({...formData, last_name: e.target.value})} />
                  </div>
                </div>
                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Email *</label>
                    <input style={styles.input} type="email" required value={formData.email}
                      onChange={e => setFormData({...formData, email: e.target.value})} />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Phone</label>
                    <input style={styles.input} value={formData.phone}
                      onChange={e => setFormData({...formData, phone: e.target.value})} />
                  </div>
                </div>
                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Job Title</label>
                    <input style={styles.input} value={formData.job_title || ''}
                      placeholder="e.g. Director / CEO, Field Technician"
                      onChange={e => setFormData({...formData, job_title: e.target.value})} />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Role *</label>
                    <select style={styles.input} value={formData.role}
                      onChange={e => setFormData({...formData, role: e.target.value})}>
                      <option value="technician">Technician</option>
                      <option value="manager">Manager</option>
                      <option value="router">Router</option>
                      <option value="supervisor">Supervisor</option>
                      <option value="customer_service">Customer Service</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div style={{...styles.formGroup, display: 'flex', alignItems: 'flex-end', paddingBottom: '4px'}}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={formData.is_technician}
                        onChange={e => setFormData({...formData, is_technician: e.target.checked})}
                        style={{ width: '20px', height: '20px', accentColor: '#2D5C3F' }} />
                      <span style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>Can perform field services</span>
                    </label>
                  </div>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>{editingTech ? 'New Password (leave blank to keep current)' : 'Password *'}</label>
                  <input style={styles.input} type="password" required={!editingTech} value={formData.password}
                    onChange={e => setFormData({...formData, password: e.target.value})} />
                </div>

                {/* REPORTS TO — org hierarchy, visible para todos los roles */}
                <div style={styles.formGroup}>
                  <label style={styles.label}>Reports To (Organizational Hierarchy)</label>
                  <select style={styles.input} value={formData.supervisor_id || ''}
                    onChange={e => setFormData({...formData, supervisor_id: e.target.value ? parseInt(e.target.value) : ''})}>
                    <option value="">-- No direct supervisor (top level) --</option>
                    {supervisors
                      .filter(s => {
                        // Mostrar todos los usuarios activos excepto el mismo usuario
                        // El admin puede reportar a super_admin, todos los demás pueden reportar a cualquiera de rol superior
                        const roleOrder = { technician: 1, router: 1, customer_service: 1, supervisor: 2, manager: 3, admin: 4, super_admin: 5 };
                        const currentRoleLevel = roleOrder[formData.role] || 1;
                        const supervisorRoleLevel = roleOrder[s.role] || 1;
                        return supervisorRoleLevel >= currentRoleLevel;
                      })
                      .map(s => (
                        <option key={s.id} value={s.id}>
                          {s.first_name} {s.last_name} — {s.role.charAt(0).toUpperCase() + s.role.slice(1)}
                        </option>
                      ))
                    }
                  </select>
                  <p style={{ fontSize: '12px', color: '#6B7280', margin: '4px 0 0' }}>
                    Used for report notifications and approval workflow
                  </p>
                </div>

                {/* LICENSE SECTION - only when editing AND is_technician */}
                {editingTech && formData.is_technician && (
                  <div style={{ marginTop: '24px', borderTop: '2px solid #E5E7EB', paddingTop: '20px' }}>
                    
                    {/* Has own license checkbox */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                      <input type="checkbox" checked={hasLicense}
                        onChange={e => handleHasLicenseChange(e.target.checked)}
                        style={{ width: '20px', height: '20px', accentColor: '#2D5C3F' }} />
                      <span style={{ fontSize: '15px', fontWeight: '600', color: '#111827' }}>
                        Has own pest control license
                      </span>
                    </div>

                    {/* IF HAS LICENSE: show license management */}
                    {hasLicense && (
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                          <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#2D5C3F' }}>
                            Licenses ({licenses.length})
                          </h4>
                          {!showLicenseForm && (
                            <button type="button" onClick={() => openLicenseForm()} style={{
                              backgroundColor: '#2D5C3F', color: '#fff', border: 'none', padding: '6px 14px',
                              borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '13px'
                            }}>+ Add License</button>
                          )}
                        </div>

                        {/* Existing licenses */}
                        {licenses.map(lic => (
                          <div key={lic.id} style={{
                            border: '1px solid #E5E7EB', borderRadius: '10px', padding: '14px',
                            marginBottom: '12px', backgroundColor: '#FAFBFC'
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span style={{
                                  backgroundColor: '#2D5C3F', color: '#fff', padding: '3px 10px',
                                  borderRadius: '6px', fontWeight: '700', fontSize: '13px'
                                }}>{lic.state}</span>
                                <span style={{ fontWeight: '600', fontSize: '15px', color: '#111827' }}>{lic.license_number}</span>
                                <span style={{ fontSize: '12px', color: '#6B7280', backgroundColor: '#F3F4F6', padding: '2px 8px', borderRadius: '4px' }}>{lic.license_type}</span>
                              </div>
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <button type="button" onClick={() => openLicenseForm(lic)}
                                  style={{ background: 'none', border: 'none', color: '#2D5C3F', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}>Edit</button>
                                <button type="button" onClick={() => handleDeleteLicense(lic.id)}
                                  style={{ background: 'none', border: 'none', color: '#DC2626', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}>Delete</button>
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: '#6B7280', marginBottom: '8px' }}>
                              {lic.issued_date && <span>Issued: {new Date(lic.issued_date + 'T00:00:00').toLocaleDateString()}</span>}
                              {lic.expiration_date && (() => {
                                const days = Math.floor((new Date(lic.expiration_date + 'T00:00:00') - new Date()) / (1000*60*60*24));
                                const color = days < 0 ? '#DC2626' : days <= 30 ? '#DC2626' : days <= 60 ? '#D97706' : '#888';
                                const bg = days < 0 ? '#FEE2E2' : days <= 30 ? '#FEE2E2' : days <= 60 ? '#FEF3C7' : 'transparent';
                                const icon = days < 0 ? '🔴 EXPIRED' : days <= 30 ? `🔴 ${days}d left` : days <= 60 ? `🟡 ${days}d left` : `✅ ${days}d left`;
                                return <span style={{ fontSize:'11px', fontWeight:'600', color, backgroundColor:bg, padding: bg !== 'transparent' ? '2px 6px' : '0', borderRadius:'8px' }}>
                                  Expires: {new Date(lic.expiration_date + 'T00:00:00').toLocaleDateString()} — {icon}
                                </span>;
                              })()}
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                              {lic.categories.map(cat => (
                                <span key={cat.id} style={{
                                  backgroundColor: '#EEF7F0', color: '#2D5C3F', padding: '3px 8px',
                                  borderRadius: '6px', fontSize: '11px', fontWeight: '600', border: '1px solid #C6E7CD'
                                }}>{cat.code} - {cat.name}</span>
                              ))}
                            </div>
                          </div>
                        ))}

                        {licenses.length === 0 && !showLicenseForm && (
                          <div style={{ textAlign: 'center', padding: '20px', color: '#9CA3AF', fontSize: '14px' }}>
                            No licenses added yet. Click "+ Add License" to add one.
                          </div>
                        )}

                        {/* License Add/Edit Form */}
                        {showLicenseForm && (
                          <div style={{
                            border: '2px solid #2D5C3F', borderRadius: '10px', padding: '16px',
                            marginTop: '12px', backgroundColor: '#F0FDF4'
                          }}>
                            <h5 style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: '700', color: '#2D5C3F' }}>
                              {editingLicense ? 'Edit License' : 'New License'}
                            </h5>
                            <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                              <div style={{ flex: '0 0 80px' }}>
                                <label style={styles.label}>State *</label>
                                <select style={styles.input} value={licenseForm.state}
                                  onChange={e => handleStateChange(e.target.value)}>
                                  <option value="NY">NY</option>
                                  <option value="NJ">NJ</option>
                                </select>
                              </div>
                              <div style={{ flex: 1 }}>
                                <label style={styles.label}>License # *</label>
                                <input style={styles.input} value={licenseForm.license_number}
                                  onChange={e => setLicenseForm({...licenseForm, license_number: e.target.value})} />
                              </div>
                              <div style={{ flex: 1 }}>
                                <label style={styles.label}>Type *</label>
                                <select style={styles.input} value={licenseForm.license_type}
                                  onChange={e => setLicenseForm({...licenseForm, license_type: e.target.value, category_ids: []})}>
                                  {(LICENSE_TYPES[licenseForm.state] || []).map(t => (
                                    <option key={t} value={t}>{t}</option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '12px', marginBottom: '14px' }}>
                              <div style={{ flex: 1 }}>
                                <label style={styles.label}>Issued Date</label>
                                <input style={styles.input} type="date" value={licenseForm.issued_date}
                                  onChange={e => setLicenseForm({...licenseForm, issued_date: e.target.value})} />
                              </div>
                              <div style={{ flex: 1 }}>
                                <label style={styles.label}>Expiration Date</label>
                                <input style={styles.input} type="date" value={licenseForm.expiration_date}
                                  onChange={e => setLicenseForm({...licenseForm, expiration_date: e.target.value})} />
                              </div>
                            </div>
                            <div style={{ marginBottom: '14px' }}>
                              <label style={styles.label}>Categories ({licenseForm.state})</label>
                              <div style={{
                                border: '1px solid #D1D5DB', borderRadius: '8px', padding: '10px',
                                backgroundColor: '#fff', maxHeight: '160px', overflowY: 'auto'
                              }}>
                                {filteredCategories.map(cat => (
                                  <label key={cat.id} style={{
                                    display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 4px',
                                    cursor: 'pointer', borderRadius: '4px', fontSize: '13px', color: '#374151'
                                  }}>
                                    <input type="checkbox" checked={licenseForm.category_ids.includes(cat.id)}
                                      onChange={() => toggleCategory(cat.id)}
                                      style={{ width: '16px', height: '16px', accentColor: '#2D5C3F' }} />
                                    <span style={{ fontWeight: '600', minWidth: '40px' }}>{cat.code}</span>
                                    <span>{cat.name}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                              <button type="button" onClick={() => { setShowLicenseForm(false); setEditingLicense(null); }}
                                style={{ padding: '8px 16px', border: '1px solid #D1D5DB', borderRadius: '6px', backgroundColor: '#fff', cursor: 'pointer', fontWeight: '500', fontSize: '13px' }}>
                                Cancel
                              </button>
                              <button type="button" onClick={handleLicenseSubmit}
                                style={{ padding: '8px 16px', border: 'none', borderRadius: '6px', backgroundColor: '#2D5C3F', color: '#fff', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}>
                                {editingLicense ? 'Update License' : 'Save License'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* IF NO LICENSE: warning — supervisor set via "Reports To" above */}
                    {!hasLicense && (
                      <div style={{
                        border: '1px solid #FDE68A', borderRadius: '10px', padding: '14px 16px',
                        backgroundColor: '#FFFBEB', display: 'flex', alignItems: 'flex-start', gap: '10px'
                      }}>
                        <span style={{ fontSize: '18px', flexShrink: 0 }}>{'⚠️'}</span>
                        <div>
                          <div style={{ fontSize: '14px', fontWeight: '600', color: '#92400E', marginBottom: '4px' }}>
                            This technician requires a licensed supervisor to perform services
                          </div>
                          <div style={{ fontSize: '12px', color: '#92400E' }}>
                            Service reports will use the license of the supervisor assigned in "Reports To" above.
                            The system will look up the hierarchy until it finds a valid pest control license.
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div style={styles.formActions}>
                  <button type="button" onClick={() => setShowModal(false)} style={styles.cancelBtn}>Cancel</button>
                  <button type="submit" style={styles.submitBtn}>
                    {editingTech ? 'Update' : 'Create'} Team Member
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        )}
      </div>
      {confirmDialog && (
        <ConfirmDialog
          message={confirmDialog.message}
          confirmText={confirmDialog.confirmText}
          confirmColor={confirmDialog.confirmColor || '#DC2626'}
          icon={confirmDialog.icon || '⚠'}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog(null)}
        />
      )}
    </Layout>

  );
};

const styles = {
  page: { maxWidth: '1200px', margin: '0 auto' },
  pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
  pageTitle: { margin: 0, fontSize: '24px', fontWeight: '700', color: '#111827' },
  pageSubtitle: { margin: '4px 0 0', fontSize: '14px', color: '#6B7280' },
  addBtn: {
    backgroundColor: '#2D5C3F', color: '#FFFFFF', border: 'none', padding: '10px 20px',
    borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px',
  },
  loading: { textAlign: 'center', padding: '60px', color: '#6B7280', fontSize: '16px' },
  tableContainer: {
    backgroundColor: '#FFFFFF', borderRadius: '12px', border: '1px solid #E5E7EB',
    overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600',
    color: '#6B7280', backgroundColor: '#F9FAFB', borderBottom: '1px solid #E5E7EB',
    textTransform: 'uppercase', letterSpacing: '0.5px',
  },
  tr: { borderBottom: '1px solid #F3F4F6', transition: 'background 0.1s' },
  td: { padding: '12px 16px', fontSize: '14px', color: '#374151' },
  nameCell: { display: 'flex', alignItems: 'center', gap: '10px' },
  avatar: {
    width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#2D5C3F',
    color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '12px', fontWeight: '700', flexShrink: 0,
  },
  nameText: { fontWeight: '500' },
  badge: {
    padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600',
    display: 'inline-block', textTransform: 'capitalize',
  },
  actionBtn: {
    background: 'none', border: 'none', color: '#2D5C3F', cursor: 'pointer',
    fontWeight: '500', fontSize: '13px', padding: '4px 8px',
  },
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  },
  modal: {
    backgroundColor: '#FFFFFF', borderRadius: '12px',
    maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  modalHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '20px 24px', borderBottom: '1px solid #E5E7EB',
  },
  modalTitle: { margin: 0, fontSize: '18px', fontWeight: '700', color: '#111827' },
  closeBtn: {
    background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer',
    color: '#6B7280', padding: '4px',
  },
  form: { padding: '24px' },
  formRow: { display: 'flex', gap: '16px', marginBottom: '16px' },
  formGroup: { flex: 1, marginBottom: '16px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' },
  input: {
    width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB', borderRadius: '8px',
    fontSize: '14px', outline: 'none', boxSizing: 'border-box',
  },
  formActions: { display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #E5E7EB' },
  cancelBtn: {
    padding: '10px 20px', border: '1px solid #D1D5DB', borderRadius: '8px',
    backgroundColor: '#FFFFFF', cursor: 'pointer', fontWeight: '500', fontSize: '14px', color: '#374151',
  },
  submitBtn: {
    padding: '10px 24px', border: 'none', borderRadius: '8px',
    backgroundColor: '#2D5C3F', color: '#FFFFFF', cursor: 'pointer', fontWeight: '600', fontSize: '14px',
  },
};

export default Technicians;
